import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useAuthStore, useUserStore } from '../../stores';
import { logException } from '../eventsLogger';
import { getLookerConfig } from '../lookerConfig';
import { getRuntimeAppConfig } from '../runtimeConfig';
import { getBackendToken, getRefreshToken } from '../tokenProvider';
import { StudioAuthRoutes } from './auth';
import { ApiConfigs, getApiConfig, type ServiceName } from './config';

/**
 * Resolve the Looker host URL for the current runtime.
 *
 * Read from `window.LOOKER_CONFIG.hostUrl` (set by the Angular host).
 *
 * IMPORTANT: this is a function (not a module-level `const`) because the
 * bundle can be imported before Angular sets `window.LOOKER_CONFIG` — a
 * top-level read would capture an empty string forever.
 */
export const getHostURL = (): string => getLookerConfig().hostUrl;

const WS_URLS = {
  local: 'http://localhost:3000',
  external: 'https://node-bq-service-k4oeqebfqq-uk.a.run.app',
};

interface Tokens {
  token: string | null;
  refreshToken: string | null;
}

interface RefreshTokenApiResponse {
  access_token?: string;
  refresh_token?: string;
}

// TODO: Remove after migrating to Multisocket WS Client
export const getWSBaseURL = (): string => {
  const runtime = getRuntimeAppConfig();
  if (runtime?.wsUrl) return runtime.wsUrl;
  return process.env.BACKEND_ENV === 'localhost' ? WS_URLS.local : WS_URLS.external;
};

export const getServiceURL = (serviceName: ServiceName): string => {
  const config = getApiConfig();

  // Dev harness: when `USE_DEV_PROXY` is set, return an empty base so
  // axios uses relative URLs (e.g. `/v7/looker/...`). The rspack dev
  // server proxies those paths to bi-api-dev, which sidesteps CORS
  // because the browser sees a same-origin request.
  if (serviceName === 'studioApi' && process.env.USE_DEV_PROXY === 'true') {
    return '';
  }

  return process.env.BACKEND_ENV === 'localhost'
    ? ApiConfigs.local[serviceName]
    : config[serviceName] || ApiConfigs.prod[serviceName];
};

export const isTokenValid = (token: string | null | undefined): boolean => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;
    return (decoded.exp || 0) > currentTime;
  } catch {
    return false;
  }
};

const MAX_TOKEN_RETRY = 3;
let tokenRetryCount = 0;

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export class BaseApi {
  private readonly api: AxiosInstance;
  private refreshPromise: Promise<Tokens | null> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: getServiceURL('studioApi'),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Exchange a refresh token for a new access/refresh token pair against
   * `studio-api` `/auth/token/refresh`.
   *
   * Uses a bare axios call (no interceptors) to avoid recursion through the
   * shared `api` instance.
   */
  public static async refreshTokens(refreshToken: string): Promise<Tokens | null> {
    if (!refreshToken) return null;

    // Dev-harness only: bi-api's public gateway rejects the refresh call
    // without an API-key identity ("UNAUTHENTICATED: Method doesn't allow
    // unregistered callers"). The harness stashes the studio_api_secret
    // on `window.LOOKER_CONFIG.apiKey` so we can attach it here. In
    // production this field is unset — the Angular host mints tokens for
    // us and refresh calls flow through its authenticated proxy.
    const apiKey = typeof window !== 'undefined' ? window.LOOKER_CONFIG?.apiKey : undefined;

    try {
      const response = await axios.post<RefreshTokenApiResponse>(
        `${getServiceURL('studioApi')}${StudioAuthRoutes.refreshToken()}`,
        { refresh_token: refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(apiKey ? { 'X-API-Key': apiKey } : {}),
          },
        },
      );

      const body = response.data ?? {};
      if (!body.access_token) return null;

      return {
        token: body.access_token,
        refreshToken: body.refresh_token ?? refreshToken,
      };
    } catch (error) {
      logException(error instanceof Error ? error : new Error(String(error)), {
        context: 'refreshTokens',
      });
      return null;
    }
  }

  private readonly saveNewTokensInStore = (tokens: Tokens) => {
    const authStore = useAuthStore.getState();

    if (tokens.token) authStore.setAccessToken(tokens.token);
    if (tokens.refreshToken) authStore.setRefreshToken(tokens.refreshToken);
  };

  private async refreshTokensOnce(refreshToken: string): Promise<Tokens | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = BaseApi.refreshTokens(refreshToken)
        .then((tokens) => {
          if (tokens) this.saveNewTokensInStore(tokens);
          return tokens;
        })
        .finally(() => {
          this.refreshPromise = null;
        });
    }
    return this.refreshPromise;
  }

  // biome-ignore lint: private method called in constructor
  private setupInterceptors() {
    this.api.interceptors.request.use(async (config) => {
      const userStore = useUserStore.getState();

      let currentToken = getBackendToken();
      const currentRefreshToken = getRefreshToken();

      // Proactively refresh if access token is expired but refresh token is still valid.
      if (currentToken && !isTokenValid(currentToken) && isTokenValid(currentRefreshToken)) {
        const refreshed = await this.refreshTokensOnce(currentRefreshToken as string);
        if (refreshed?.token) {
          currentToken = refreshed.token;
        }
      }

      const instanceURL = userStore.lookerURL || getHostURL() || '';

      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      config.headers.looker_user_id = userStore.userID || '';
      config.headers.looker_user_email = userStore.userEmail;
      config.headers.looker_instance_url = instanceURL;

      // New BI Headers
      config.headers.bi_user_id = userStore.embeddedUserId || '';
      config.headers.bi_user_email = userStore.userEmail;
      config.headers.bi_looker_url = instanceURL;

      if (config.method === 'get') {
        if (!config.params) {
          config.params = {};
        }

        if (!('referrerURL' in config.params)) {
          config.params.referrerURL = instanceURL;
        }

        if (config.params.workspaceID) {
          config.headers.bi_workspace_id = config.params.workspaceID.toString();
        }
      } else {
        if (typeof config.data === 'string') {
          try {
            config.data = JSON.parse(config.data);
          } catch (_e) {
            // Not a JSON string, do nothing
          }
        }

        if (typeof config.data === 'object' && config.data !== null && !('referrerURL' in config.data)) {
          config.data.referrerURL = instanceURL;
        }

        if (config.data.workspaceID) {
          config.headers.bi_workspace_id = config.data.workspaceID.toString();
        }
      }

      return config;
    });

    this.api.interceptors.response.use(
      (response) => {
        tokenRetryCount = 0;
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;
        if (!originalRequest) {
          return Promise.reject(error);
        }

        const currentRefreshToken = getRefreshToken();

        if (
          (error.response?.status === 401 || error.response?.status === 403) &&
          !originalRequest._retry &&
          tokenRetryCount < MAX_TOKEN_RETRY &&
          currentRefreshToken
        ) {
          originalRequest._retry = true;
          tokenRetryCount++;

          const newTokens = await this.refreshTokensOnce(currentRefreshToken);

          if (newTokens?.token) {
            originalRequest.headers.Authorization = `Bearer ${newTokens.token}`;
            tokenRetryCount = 0;
            return this.api(originalRequest);
          }
        }

        let errorMessage: string;
        const errorProperties: Record<string, unknown> = {
          url: originalRequest.url,
          axiosMessage: error.message,
        };

        if (error.response) {
          errorMessage = `API Server Error: ${error.response.status} for ${originalRequest.url}`;
          errorProperties.status = error.response.status;
          errorProperties.responseData = error.response.data;
        } else if (error.request) {
          errorMessage = `API Network Error for ${originalRequest.url}: ${error.message}`;
        } else {
          errorMessage = `API Setup Error for ${originalRequest.url}: ${error.message}`;
        }

        const contextualError = new Error(errorMessage);
        logException(contextualError, errorProperties);

        return Promise.reject(error);
      },
    );
  }

  public getAxiosInstance(): AxiosInstance {
    return this.api;
  }
}

export const baseApi = new BaseApi();
export const api = baseApi.getAxiosInstance();
export const refreshTokens = BaseApi.refreshTokens;
